import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MobileLayout from "@/components/MobileLayout";
import OnboardingStep from "@/components/OnboardingStep";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLanguage } from "@/i18n/LanguageProvider";
import LanguageSelector from "@/components/LanguageSelector";
import { useOnboardingStarted } from "@/hooks/useOnboardingStarted";
import { supabase } from "@/integrations/supabase/client";
import { Globe } from "lucide-react";

const OnboardingIdentity = () => {
  const navigate = useNavigate();
  const { t, lang, setLang } = useLanguage();
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [sex, setSex] = useState("");
  const [langOpen, setLangOpen] = useState(false);

  // onboarding_started fires once per session at the onboarding entry (deduped
  // inside the hook). Identity is the usual first screen, but the same hook on
  // OnboardingAvatar covers the paths that skip Identity (signup -> avatar,
  // logged-in-without-avatar, deep-link) — that gap was the under-count.
  useOnboardingStarted("identity");

  // Pre-fill the name from the provider's profile (Apple persists it into
  // user_metadata.full_name on first login via Auth.tsx; Google populates it
  // natively). Same pattern as CheckoutPremium.tsx. Name/age/sex are all
  // optional (Apple Guideline 5.1.1(v) — biological sex + age must not be
  // required), so this only seeds a convenience default.
  useEffect(() => {
    let cancelled = false;
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (cancelled || !user) return;
      const fullName = (user.user_metadata as Record<string, unknown> | undefined)?.full_name;
      if (typeof fullName === "string" && fullName.length > 0) {
        // Only seed if the user hasn't already started typing.
        setName((prev) => (prev.length > 0 ? prev : fullName));
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Avaturn preconnect removed W4 (provider deprecated; MP catalog uses
  // Supabase Storage which is already preconnected at the app level).

  const genderOptions = [
    { value: "male", label: t("onboarding.identity.male") },
    { value: "female", label: t("onboarding.identity.female") },
    { value: "other", label: t("onboarding.identity.other") },
  ];

  // Apple Guideline 5.1.1(v): biological sex + age must NOT be required, and
  // name was flagged in round 1 too. Continue is therefore always enabled —
  // empty fields persist as NULL (see Processing.tsx).

  const handleNext = () => {
    localStorage.setItem("vyv_onboarding", JSON.stringify({ name, age, sex }));
    navigate("/onboarding/avatar");
  };

  return (
    <MobileLayout className="py-8">
      {/* Language selector */}
      <div className="absolute top-4 right-4 z-20">
        <div className="relative">
          <button
            onClick={() => setLangOpen(!langOpen)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-secondary/60 border border-border/40 text-muted-foreground hover:text-foreground transition-colors text-xs"
          >
            <Globe className="w-3.5 h-3.5" />
            <span className="font-display font-medium">{lang}</span>
          </button>
          {langOpen && (
            <div className="absolute right-0 top-full mt-1 bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50">
              <LanguageSelector variant="compact-menu" onSelect={() => setLangOpen(false)} />
            </div>
          )}
        </div>
      </div>

      <OnboardingStep step={1} totalSteps={2}
        title={t("onboarding.identity.title")}
        subtitle={t("onboarding.identity.subtitle")}>
        <div className="space-y-3">
          <Input placeholder={t("onboarding.identity.firstName")} value={name} onChange={(e) => setName(e.target.value)}
            className="bg-secondary/60 border-border/50 text-foreground placeholder:text-muted-foreground h-13 rounded-xl" />
          <div className="grid grid-cols-2 gap-3">
            <Input type="number" placeholder={t("onboarding.identity.age")} value={age} onChange={(e) => setAge(e.target.value)}
              className="bg-secondary/60 border-border/50 text-foreground placeholder:text-muted-foreground h-13 rounded-xl" />
            <div className="flex gap-1.5">
              {genderOptions.map((opt) => (
                <button key={opt.value} onClick={() => setSex(opt.value)}
                  className={`flex-1 h-13 rounded-xl font-display text-xs font-medium transition-all duration-300 ${
                    sex === opt.value ? "bg-primary/15 text-primary border border-primary/30"
                      : "bg-secondary/60 text-muted-foreground border border-transparent hover:bg-muted"
                  }`}>{opt.label}</button>
              ))}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground px-1">
            {t("onboarding.identity.optionalHint")}
          </p>
        </div>
        <div className="mt-auto pt-8">
          <Button variant="hero" size="xl" className="w-full" soundOnPress={false} onClick={handleNext}>
            {t("onboarding.continue")}
          </Button>
        </div>
      </OnboardingStep>
    </MobileLayout>
  );
};

export default OnboardingIdentity;
